import type { TFunction } from 'i18next'
import type {
  EditorTextBoxConversionReport,
  EditorTextBoxConversionReportEntry,
} from './tinymce-conversion'
import type { EditorPageSetTurnoverReportEntry } from './editorPageSetTurnoverRun'

/** How many losses the toast names before it counts the rest toward the log. */
const NAMED_LOSSES = 3

/** The items as one list joined with "and", in the given language's words. */
export const editorTextBoxListText = (
  items: readonly string[],
  language: string | undefined,
): string =>
  new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(
    items,
  )

/** One thing a conversion did not carry, in the words the toast names it by. */
export const editorTextBoxConversionEntryText = (
  entry: EditorTextBoxConversionReportEntry,
  t: TFunction,
): string => {
  switch (entry.kind) {
    case 'frozen-field':
      return t('editor.textBox.convertFromTinyMce.entries.frozenField', {
        key: entry.key,
      })
    case 'table-frozen':
      return t('editor.textBox.convertFromTinyMce.entries.tableFrozen')
    case 'service-binding-dropped':
      return t(
        'editor.textBox.convertFromTinyMce.entries.serviceBindingDropped',
      )
    case 'additional-family-more-detail':
      return t(
        'editor.textBox.convertFromTinyMce.entries.additionalFamilyMoreDetail',
      )
    case 'name-styled-as-one-run':
      return t('editor.textBox.convertFromTinyMce.entries.nameStyledAsOneRun')
    case 'placeholder-only':
      return t('editor.textBox.convertFromTinyMce.entries.placeholderOnly')
    case 'shadow-skipped':
      return t('editor.textBox.convertFromTinyMce.entries.shadowSkipped')
    case 'highlight-dropped':
      return t('editor.textBox.convertFromTinyMce.entries.highlight')
    case 'family-outside-picker':
      return t(
        'editor.textBox.convertFromTinyMce.entries.familyOutsidePicker',
        {
          family: entry.family,
        },
      )
    case 'edit-dropped':
      switch (entry.edit) {
        case 'background':
          return t('editor.textBox.convertFromTinyMce.entries.background')
        case 'opacity':
          return t('editor.textBox.convertFromTinyMce.entries.opacity')
        case 'border':
          return t('editor.textBox.convertFromTinyMce.entries.border')
        case 'drop-shadow':
          return t('editor.textBox.convertFromTinyMce.entries.dropShadow')
      }
  }
}

/**
 * Up to three entries in the report's order and a count of the rest, which
 * the log holds in full, joined in the language the copy resolved to.
 */
const namedEntries = (
  texts: readonly string[],
  t: TFunction,
  language: string | undefined,
): string => {
  const named = texts.slice(0, NAMED_LOSSES)
  const counted = texts.length - named.length
  return editorTextBoxListText(
    counted > 0
      ? [
          ...named,
          t('editor.textBox.convertFromTinyMce.more', { count: counted }),
        ]
      : named,
    language,
  )
}

/**
 * The one sentence a conversion's toast says: that the box converted and
 * Save changes writes the page, and, when something did not carry, the
 * first three losses in the report's order and a count of the rest.
 */
export const editorTextBoxConversionToastText = (
  report: EditorTextBoxConversionReport,
  t: TFunction,
  language: string | undefined,
): string => {
  if (report.length === 0)
    return t('editor.textBox.convertFromTinyMce.converted')
  const entries = namedEntries(
    report.map((entry) => editorTextBoxConversionEntryText(entry, t)),
    t,
    language,
  )
  return t('editor.textBox.convertFromTinyMce.convertedExcept', { entries })
}

/**
 * One entry of a page-set turnover's report in the words its toast names it
 * by: the skipped boxes with the pages they sit on, the frozen fields of
 * every box counted together, or a converter entry as the one-box toast
 * words it.
 */
export const editorPageSetTurnoverEntryText = (
  entry: EditorPageSetTurnoverReportEntry,
  t: TFunction,
  language: string | undefined,
): string => {
  switch (entry.kind) {
    case 'skipped':
      return t(
        entry.pageNumbers.length === 1
          ? 'editor.textBox.turnover.entries.skippedOnPage'
          : 'editor.textBox.turnover.entries.skippedOnPages',
        {
          count: entry.count,
          pages: editorTextBoxListText(entry.pageNumbers.map(String), language),
        },
      )
    case 'frozen-fields':
      return t('editor.textBox.turnover.entries.frozenFields', {
        count: entry.count,
      })
    default:
      return editorTextBoxConversionEntryText(entry, t)
  }
}

/**
 * The one sentence a page-set turnover's toast says: how many boxes
 * converted, and, when something was skipped or did not carry, the first
 * three entries of its report, skipped boxes first, and a count of the rest.
 */
export const editorPageSetTurnoverToastText = (
  convertedCount: number,
  report: readonly EditorPageSetTurnoverReportEntry[],
  t: TFunction,
  language: string | undefined,
): string => {
  if (report.length === 0)
    return t('editor.textBox.turnover.converted', { count: convertedCount })
  const entries = namedEntries(
    report.map((entry) => editorPageSetTurnoverEntryText(entry, t, language)),
    t,
    language,
  )
  return t('editor.textBox.turnover.convertedExcept', {
    count: convertedCount,
    entries,
  })
}
